'use client'

import Link from 'next/link'
import Image from 'next/image'
import { World } from '@desoutter/uikit'
import { ExternalLinkRecord } from '@/graphql/types'
import { usePathname, useSearchParams } from 'next/navigation'
import { Suspense } from 'react'

const isValidLocalePath = (path) => /^\/[a-z]{2}(_[A-Z]{2})?$/.test(path)

const LinkLocaleSwitcher = ({ locale }: { locale: ExternalLinkRecord }) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()

	const getLocalePath = (locale: ExternalLinkRecord) => {
		try {
			const address = new URL(locale.url)
			return address.pathname
		} catch {
			return '/'
		}
	}
	const localePath = getLocalePath(locale)
	const localeDefault = pathname.split('/')[1] || ''

	// Modification de la construction des paramètres d'URL
	const params = new URLSearchParams(searchParams)
	// Supprime le paramètre 'locale' s'il existe pour éviter les conflits
	params.delete('locale')
	const localeParams = params.toString()
	const queryString = localeParams ? `?${localeParams}` : ''

	const href =
		(isValidLocalePath(localePath)
			? pathname.replace(
					`/${localeDefault}`,
					`${localePath.toLowerCase().replace('_', '-')}`,
				)
			: locale.url) + queryString

	return (
		<li className="px-2 py-1 rounded text-sm text-gray-400">
			<Link
				prefetch={false}
				rel="nofollow"
				href={href}
				title={locale.label}
				data-datocms-noindex="true"
			>
				<div className="flex items-center ">
					<Image
						src={locale.icon?.url || '/images/image-placeholder.svg'}
						className="mr-2"
						alt={locale.icon?.alt || locale.label || 'Language flag'}
						width="16"
						height="16"
					/>
					<div>{locale.label}</div>
				</div>
			</Link>
		</li>
	)
}

export default function HeaderLocaleSwitcher({
	locales,
}: {
	locales: ExternalLinkRecord[]
}) {
	return (
		<div
			className="relative group text-[var(--menu-text-foreground)] flex items-center"
			tabIndex={6}
		>
			<button className="relative z-10" title="language">
				<World />
			</button>
			<Suspense>
				<div className="hidden group-focus-within:block absolute top-12 right-0 px-1 py-2 bg-gray-50 rounded-2xl z-50">
					<ul className="w-44 max-h-64 px-1 overflow-y-auto scrollbar scrollbar-thumb-primary scrollbar-track-gray-100 scrollbar-w-0.5 scrollbar-thumb-rounded-full scrollbar-track-rounded-full">
						{locales?.map((locale) => (
							<LinkLocaleSwitcher key={locale.id} locale={locale} />
						))}
					</ul>
				</div>
			</Suspense>
		</div>
	)
}
